/**
 * PDP fulfilment core — shared byte-for-byte by the BN and ET themes.
 *
 * Pure functions only: no DOM, no network, no storage. Each theme's controller
 * owns its markup and copy and asks this file two questions:
 *   stockState()    what can we honestly say about stock for this quantity?
 *   dispatchState() when will an in-stock order leave the warehouse?
 *
 * The component is informational. Nothing here changes inventory, prices,
 * purchase availability or cart contents; Shopify stays the authority at
 * add-to-cart and checkout.
 *
 * Regression tests: tests/pdp-fulfilment.test.js at the repository root.
 */
(function (global) {
  'use strict';

  // Themes publish quantities capped at this value, so a page never discloses
  // exact large holdings. A capped quantity means "at least STOCK_CAP".
  const STOCK_CAP = 100;

  /**
   * Stock state for one variant and a requested quantity (including matching
   * units already in the cart). Untracked variants have no meaningful
   * quantity, so nothing is inferred from it.
   *
   * Kinds: unavailable | untracked | stocked | low | limited | supplier.
   * A threshold of 0 disables the "low" state entirely.
   */
  function stockState(variant, requested = 1, threshold = 2) {
    if (!variant || !variant.available) return { kind: 'unavailable' };
    const qty = variant.inventory_quantity;
    if (!variant.inventory_management || typeof qty !== 'number' || !Number.isFinite(qty)) {
      return { kind: 'untracked' };
    }
    const wanted = Math.max(1, Math.floor(Number(requested) || 1));
    if (qty < wanted && qty < STOCK_CAP) {
      if (variant.inventory_policy !== 'continue') {
        return { kind: qty > 0 ? 'limited' : 'unavailable', qty: Math.max(0, qty) };
      }
      return {
        kind: 'supplier',
        qty: Math.max(0, qty),
        mixed: qty > 0,
        uncertain: Boolean(variant.lead_time_unconfirmed)
      };
    }
    return { kind: qty <= Math.max(0, Number(threshold) || 0) ? 'low' : 'stocked', qty };
  }

  const DAY = 86400000;
  const utc = (y, m, d) => new Date(Date.UTC(y, m - 1, d));
  const iso = date => date.toISOString().slice(0, 10);
  const addDays = (date, n) => new Date(date.getTime() + n * DAY);
  const isWeekend = date => date.getUTCDay() === 0 || date.getUTCDay() === 6;
  const nextMonday = date => addDays(date, (8 - date.getUTCDay()) % 7 || 7);

  /** Gregorian Easter Sunday (Meeus/Jones/Butcher). */
  function easterSunday(year) {
    const a = year % 19, b = Math.floor(year / 100), c = year % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    return utc(year, month, ((h + l - 7 * m + 114) % 31) + 1);
  }

  function nthMonday(year, month, n) {
    const first = utc(year, month, 1);
    return addDays(first, (8 - first.getUTCDay()) % 7 + 7 * (n - 1));
  }

  /**
   * NSW statewide public holidays for any year, from the Public Holidays Act
   * 2010 rules as published at nsw.gov.au/about-nsw/public-holidays. Computed
   * rather than listed, so the countdown never lapses at the end of a list.
   * Bank Holiday is excluded: it is not a declared public holiday.
   */
  function nswHolidays(year) {
    const days = new Set();
    const add = date => days.add(iso(date));

    // New Year's Day, plus the following Monday when it falls on a weekend.
    const newYear = utc(year, 1, 1);
    add(newYear);
    if (isWeekend(newYear)) add(nextMonday(newYear));

    // Australia Day moves to the following Monday when it falls on a weekend.
    const australiaDay = utc(year, 1, 26);
    add(isWeekend(australiaDay) ? nextMonday(australiaDay) : australiaDay);

    // Good Friday, Easter Saturday, Easter Sunday, Easter Monday.
    const easter = easterSunday(year);
    for (const offset of [-2, -1, 0, 1]) add(addDays(easter, offset));

    // Anzac Day stays on 25 April; a weekend Anzac Day adds the following Monday.
    const anzac = utc(year, 4, 25);
    add(anzac);
    if (isWeekend(anzac)) add(nextMonday(anzac));

    add(nthMonday(year, 6, 2));  // King's Birthday
    add(nthMonday(year, 10, 1)); // Labour Day

    // Christmas and Boxing Day each add the next free weekday when they fall on
    // a weekend (so a Saturday Christmas gives Mon 27 and Tue 28).
    const christmas = utc(year, 12, 25), boxingDay = utc(year, 12, 26);
    add(christmas);
    add(boxingDay);
    let extra = addDays(boxingDay, 1);
    for (const day of [christmas, boxingDay]) {
      if (!isWeekend(day)) continue;
      while (isWeekend(extra) || days.has(iso(extra))) extra = addDays(extra, 1);
      add(extra);
      extra = addDays(extra, 1);
    }
    return days;
  }

  /**
   * Dispatch state for an in-stock order placed at `now`, using warehouse
   * wall-clock time — never the shopper's timezone. `closures` adds store
   * closure dates (YYYY-MM-DD, comma or newline separated) to the public
   * holidays. Kinds: today (with time) | next (with date) | fallback.
   */
  function dispatchState(now = new Date(), cutoff = '13:00', closures = '', timeZone = 'Australia/Sydney') {
    let parts;
    try {
      parts = Object.fromEntries(new Intl.DateTimeFormat('en-AU', {
        timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
      }).formatToParts(now).map(part => [part.type, part.value]));
    } catch (error) {
      return { kind: 'fallback' }; // Unknown timezone: make no time promise.
    }
    const match = /^(\d{1,2}):(\d{2})$/.exec(String(cutoff));
    if (!match || +match[1] > 23 || +match[2] > 59) return { kind: 'fallback' };

    const closed = new Set(String(closures || '').split(/[\s,]+/)
      .filter(value => /^\d{4}-\d{2}-\d{2}$/.test(value)));
    const holidays = new Map();
    const isWorking = date => {
      const year = date.getUTCFullYear();
      if (!holidays.has(year)) holidays.set(year, nswHolidays(year));
      const key = iso(date);
      return !isWeekend(date) && !holidays.get(year).has(key) && !closed.has(key);
    };

    let date = utc(+parts.year, +parts.month, +parts.day);
    const remaining = (+match[1] * 60 + +match[2]) - (+parts.hour * 60 + +parts.minute);
    if (isWorking(date) && remaining > 0) {
      const hours = String(Math.floor(remaining / 60)).padStart(2, '0');
      const minutes = String(remaining % 60).padStart(2, '0');
      return { kind: 'today', minutes: remaining, time: `${hours}h ${minutes}m` };
    }
    for (let days = 1; days <= 14; days++) {
      date = addDays(date, 1);
      if (isWorking(date)) {
        return {
          kind: 'next',
          iso: iso(date),
          date: new Intl.DateTimeFormat('en-AU', {
            timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short'
          }).format(date)
        };
      }
    }
    return { kind: 'fallback' }; // A fortnight of closures: make no date promise.
  }

  const api = Object.freeze({ STOCK_CAP, stockState, dispatchState, nswHolidays, easterSunday });
  global.PDPFulfilment = api;
  if (typeof module === 'object' && module.exports) module.exports = api;
})(typeof window === 'undefined' ? globalThis : window);
